import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// Batch queue to minimize DB writes
let behaviorQueue: Array<{
  user_id: string;
  action_type: string;
  action_data: Record<string, unknown>;
  page_path: string;
  session_id: string;
}> = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function getSessionId(): string {
  const key = "vt_behavior_sid";
  let sid = sessionStorage.getItem(key);
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

async function flushQueue() {
  if (behaviorQueue.length === 0) return;
  const batch = behaviorQueue.splice(0, 20); // max 20 per flush
  try {
    await supabase.from("user_behavior").insert(batch as any);
  } catch {
    // silent — behavior tracking must never break the app
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, 5000); // flush every 5 seconds
}

/**
 * Tracks user behavior in the background.
 * Logs page views automatically and provides `trackAction()` for feature clicks.
 * 
 * Usage:
 *   const { trackAction } = useTrackBehavior();
 *   trackAction("search_filter", { filter: "pet_friendly" });
 */
export function useTrackBehavior() {
  const { user } = useAuth();
  const location = useLocation();
  const lastPath = useRef<string>("");

  // Auto-track page views
  useEffect(() => {
    if (!user) return;
    const path = location.pathname;
    if (path === lastPath.current) return;
    lastPath.current = path;

    behaviorQueue.push({
      user_id: user.id,
      action_type: "page_view",
      action_data: { referrer: document.referrer || null },
      page_path: path,
      session_id: getSessionId(),
    });
    scheduleFlush();
  }, [location.pathname, user]);

  // Manual action tracking
  const trackAction = useCallback(
    (actionType: string, data: Record<string, unknown> = {}) => {
      if (!user) return;
      behaviorQueue.push({
        user_id: user.id,
        action_type: actionType,
        action_data: data,
        page_path: location.pathname,
        session_id: getSessionId(),
      });
      scheduleFlush();
    },
    [user, location.pathname]
  );

  // Flush on unmount / page hide
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushQueue();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      flushQueue();
    };
  }, []);

  // Periodically aggregate preferences (once per session, after 60s)
  const aggregated = useRef(false);
  useEffect(() => {
    if (!user || aggregated.current) return;
    const timer = setTimeout(async () => {
      aggregated.current = true;
      try {
        await supabase.rpc("aggregate_user_preferences", { _user_id: user.id });
      } catch {
        // silent
      }
    }, 60000);
    return () => clearTimeout(timer);
  }, [user]);

  return { trackAction };
}
